import React from 'react';
import {observer} from 'mobx-react';
import {observable, action, makeObservable} from 'mobx';
import {PropTypes} from 'prop-types';
import {withRouter} from '../../core/withRouter';

import {modalsStore} from '../../core/stores/modals.store';
import {utilsService} from '../../core/services/utils.service';
import AlertMessage from '../alert-message/alert-message.jsx';
import {AppModal} from '../app-modal/app-modal';
import TextInput from '../forms/text-input';
import RadioGroup from '../forms/radio-group';
import {ButterBar} from '../../components/alert-message/butter-bar';
import { AsideLink } from '../aside-link/aside-link';

@observer
class NewAppNamePlatform extends React.Component {

	static propTypes = {
		store: PropTypes.object.isRequired,
		match: PropTypes.object.isRequired
	};

	constructor(props) {
		super(props);
		makeObservable(this);
		this.master = this.props.store.newAppMasterStore;
		this.props.store.checkForNewAppData();
		this.references = {};
		this.master.newAppData.listingType = 'CROSSLISTED';
	}

	@observable deleteIsLoading = false;
	@observable submissionAttempted = false;
	@observable showAlertBar = false;
	@observable editHoverClass = '';

	handleDeleteDraftConfirm = () => {
		if (!this.deleteIsLoading === true) {
			this.deleteIsLoading = true;
			this.props.store.deleteDraft().then(() => this.deleteIsLoading = false);
		}
	};

	handleToggleModal = (modalName) => {
		modalsStore.toggleModal(modalName);
	};

	onEditClick = () => {
		this.editHoverClass = '';
		this.props.store.editSavedNameAndPlatform();
	};

	onEditMouseOver = () => {
		this.editHoverClass = 'edit-hover';
	};

	onEditMouseOut = () => {
		this.editHoverClass = '';
	};

	toggleAlert = (bool = !this.showAlertBar) => {
		this.showAlertBar = bool;
	}

	onSubmit = (event) => {
		event.preventDefault();
		this.toggleAlert(false);
		this.submissionAttempted = true;
		if(!this.props.store.isValid){
			// Show alert after render queue has finished
			setTimeout(() => {
				this.toggleAlert(true)
			}, 100)
		} else if (this.master.newAppData.saveId) {
			//user is editing existing information, don't move on.
			this.saveAndStay();
		} else {
			//user is creating a new app, save and move on
			this.saveAndContinue();
		}
	};

	saveAndStay = () => {
		if (this.props.store.isValid) {
			this.master.saveStep(0);
			this.submissionAttempted = false;
		}
	};

	saveAndContinue = () => {
		if (this.props.store.isValid) {
			this.master.saveStepAndContinue(0);
			this.submissionAttempted = false;
		}
	};

	handleStoreLinkBlur = () => {
		const valueBeforeBlur = this.master.newAppData.storeLink;
		if (valueBeforeBlur && !this.master.HYPERLINK_PREFIX.test(valueBeforeBlur)) {
			this.props.store.updateValue('storeLink', 'http://' + valueBeforeBlur)
		}
	}

	handleStoreLinkUpdate = (value) => {
		this.props.store.updateValue('storeLink', value);
		this.props.store.storeLinkBreaksFormatCriterion = this.references.storeLink && this.references.storeLink.breaksFormatCriterion;
	}

	renderControlButtons = () => {
		let secondaryButtonText, secondaryButtonAction;
		if(this.master.newAppData.saveId){
			//Existing Drafts and New Copies
			secondaryButtonText = 'Discard Changes & Close';
			secondaryButtonAction = () => {
				this.props.store.discardChangesAndClose();
			};
		} else {
			//New Drafts
			secondaryButtonText = 'Discard & Go Back';
			secondaryButtonAction = () => {
				this.props.store.discardAndGoBack();
			}
		}

		let markup = (
			<div className="new-app-name-platform-buttons">
				<button type="submit" className={`fn-primary ${!this.props.store.isValid && 'disabled'}`}>
					Save &amp; Continue
				</button>
				<button type="button" className="fn-secondary" onClick={secondaryButtonAction}>
					{secondaryButtonText}
				</button>
			</div>
		);

		return markup;
	}

	@action platformSelected(platform) {
		if (platform === 'App Store') this.master.newAppData.listingType =  'CROSSLISTED';
	}

	renderCrossListedForApple(storeOptionsString, storeName) {
		this.platformSelected(storeName);
		
		return (
		<div>
		<p>Your app will be cross-listed in the FirstNet App Catalog.</p>
		<TextInput
			id="storeLink"
			type="text"
			ref={(i) => this.references['storeLink'] = i}
			labelText={`${storeName} URL`}
			helperText={`App name must match the name of the app in ${storeOptionsString}.`}
			value={this.master.newAppData.storeLink}
			updateValue={this.handleStoreLinkUpdate}
			onBlur={this.handleStoreLinkBlur}
			required={this.master.newAppData.listingType === 'CROSSLISTED'}
			format={{
				criterion: this.master.URL_REGEX,
				error: this.master.MALFORMED_WEBSITE_ERROR
			}}
			errorMessage={this.master.MALFORMED_WEBSITE_ERROR}
			showErrorDirectiveFromMaster={this.submissionAttempted} />
			<RadioGroup
					id="urlType"
					ref={(i) => this.references['urlType'] = i}
					value={this.master.newAppData.urlType}
					updateValue={(value) => {this.props.store.updateValue('urlType', value)}}
					required={true}
					disabled={false}
					labelText={`${storeName} URL type`}
					helperText={`Enterprise store requires a registered account.`}
					optionsList={[{
						title: 'Consumer Store',
						hiddenValue: 'C'
					},
					{
						title: 'Enterprise Store',
						hiddenValue: 'E'
					}]}
					showErrorDirectiveFromMaster={this.submissionAttempted} />
			<TextInput
				id="storeVersion"
				type="text"
				ref={(i) => this.references['storeVersion'] = i}
				labelText={`${storeName} Version`}
				helperText={`Version number must match the one in ${storeOptionsString}. Max 18 characters.`}
				charLimit={18}
				value={this.master.newAppData.storeVersion}
				updateValue={(value) => {this.props.store.updateValue('storeVersion', value)}}
				required={this.master.newAppData.listingType === 'CROSSLISTED'}
				format={{
					criterion: this.master.VERSION_REGEX,
					error: this.master.MALFORMED_VERSION_ERROR
				}}
				errorMessage={this.master.MALFORMED_VERSION_ERROR}
				showErrorDirectiveFromMaster={this.submissionAttempted} />
			</div>)
	}

	renderPlatformButton = ({value, iconClass}) => {
		const normalizedValue = value.toLowerCase();
		const isActive = this.master.newAppData.platform.toLowerCase() === normalizedValue;
		return (
			<button
				type="button"
				value={value}
				onClick={(event) => {
					//FPSE-1875 use currentTarget so event bubbles up from clicks on tags within button
					this.props.store.updateValue('platform',event.currentTarget.value);
				}}
				className={`form-control ${isActive ? 'active' : ''}`}>

				<span className="sr-only">Choose</span>
				<i className={`icon ${iconClass} ltblue`} aria-hidden="true"></i>
				{value}
				<span className="sr-only">as supported platform.</span>
			</button>
		)
	}

	renderCrossListedForAndroid(storeOptionsString, storeName) {
		//this.master.newAppData.listingType = 'CROSSLISTED';
		return (
			<React.Fragment>
				<br/>
				<p>Your app will be cross-listed in the FirstNet App Catalog.</p>
				{/* <RadioGroup
					id="listingType"
					ref={(i) => this.references['listingType'] = i}
					value={this.master.newAppData.listingType}
					updateValue={(value) => {this.props.store.updateValue('listingType', value)}}
					required={true}
					labelText="Listing type"
					helperText={`Cross-listed applies if app is listed in ${storeOptionsString}. Select hosted for FirstNet only listing.`}
					optionsList={[{
						title: 'Cross-listed',
						hiddenValue: 'CROSSLISTED'
					},
					{
						title: 'Hosted',
						hiddenValue: 'HOSTED'
					}]}
					showErrorDirectiveFromMaster={this.submissionAttempted} /> */}
				{this.master.newAppData.listingType === 'CROSSLISTED' && <TextInput
					id="storeLink"
					type="text"
					ref={(i) => this.references['storeLink'] = i}
					labelText={`${storeName} Store URL`}
					helperText={`App name must match the name of the app in ${storeOptionsString}.`}
					value={this.master.newAppData.storeLink}
					updateValue={this.handleStoreLinkUpdate}
					onBlur={this.handleStoreLinkBlur}
					required={this.master.newAppData.listingType === 'CROSSLISTED'}
					format={{
						criterion: this.master.URL_REGEX,
						error: this.master.MALFORMED_WEBSITE_ERROR
					}}
					errorMessage={this.master.MALFORMED_WEBSITE_ERROR}
					showErrorDirectiveFromMaster={this.submissionAttempted} />}
					<RadioGroup
					id="urlType"
					ref={(i) => this.references['urlType'] = i}
					value={this.master.newAppData.urlType}
					updateValue={(value) => {this.props.store.updateValue('urlType', value)}}
					required={true}
					disabled={false}
					labelText={`${storeName} Store URL type`}
					helperText={`Enterprise store requires a registered account.`}
					optionsList={[{
						title: 'Consumer Store',
						hiddenValue: 'C'
					},
					{
						title: 'Enterprise Store',
						hiddenValue: 'E'
					}]}
					showErrorDirectiveFromMaster={this.submissionAttempted} />
				{this.master.newAppData.listingType === 'CROSSLISTED' && <TextInput
					id="storeVersion"
					type="text"
					ref={(i) => this.references['storeVersion'] = i}
					labelText={`${storeName} Store Version`}
					helperText={`Version number must match the one in ${storeOptionsString}. Max 18 characters.`}
					charLimit={18}
					value={this.master.newAppData.storeVersion}
					updateValue={(value) => {this.props.store.updateValue('storeVersion', value)}}
					required={this.master.newAppData.listingType === 'CROSSLISTED'}
					format={{
						criterion: this.master.VERSION_REGEX,
						error: this.master.MALFORMED_VERSION_ERROR
					}}
					errorMessage={this.master.MALFORMED_VERSION_ERROR}
					showErrorDirectiveFromMaster={this.submissionAttempted} />}
			</React.Fragment>
		)
	}

	renderEditableFields = () => {		
		const storeName = this.master.isAndroidApp
			? 'Google Play'
			: 'App Store';
		const storeOptionsString = this.master.newAppData.platform
			? this.master.isAndroidApp
				? 'Google Play Store'
				: 'the App Store'
			: 'the App Store or Google Play';
		return (
			<form className="container" onSubmit={this.onSubmit}>
				<div className="row">
					<h1 className="col-8 offset-1">
						Submit your app
					</h1>
				</div>
				<div id="app-name-platform" className="walkthrough-submit-an-app">
					<div className="row">
						<div className="new-app-icon col-2 offset-1">
							<img src="/images/placeholder-image.png" width="166" height="166" alt="App icon placeholder"/>
						</div>
						<div className="new-app-name-fields col-8">
							<TextInput
								id="appName"
								type="text"
								ref={(i) => this.references['appName'] = i}
								required={true}
								charLimit={30}
								labelText="App Name"
								helperText="Max 30 characters."
								errorMessage="Please enter an app name"
								value={this.master.newAppData.appName}
								updateValue={(value) => {this.props.store.updateValue('appName', value)}}
								showErrorDirectiveFromMaster={this.submissionAttempted && !this.master.newAppData.appName} />
							<div className="new-app-platform form-group">
								<div className="col-form-label">
									Platform
									<span className="sr-only">This is a required field</span>
									<span className="required-asterisks"> *</span>
									<div className="msgBlock error error-list">
										<span role={this.master.newAppData.platform === '' && this.submissionAttempted ? 'status' : ''}>
											{this.master.newAppData.platform === '' && this.submissionAttempted ? 'Please select a platform' : ''}
										</span>
									</div>
								</div>
								{this.renderPlatformButton({value: 'iOS', iconClass: 'icon-apple'})}
								{this.renderPlatformButton({value: 'Android', iconClass: 'icon-android'})}
							</div>
							{ (this.master.newAppData.platform !== "") ? (this.master.isAndroidApp ? this.renderCrossListedForAndroid(storeOptionsString, storeName) : this.renderCrossListedForApple(storeOptionsString, storeName)) : ""}
						</div>
					</div>
					<div className="pre-submission-form row">
						<div className="col-12">
							{this.renderControlButtons()}
						</div>
					</div>
				</div>
			</form>
		);
	}

	renderRecordedFields = () => {
		const storeName = this.master.isAndroidApp
		? 'Google Play'
		: 'App Store';
		const lastSavedDatetime = this.master.newAppData.lastSavedDatetime;
		return (
			<div className="saved-app-name-and-platform walkthrough-submit-an-app container">
				<div className="row info-section">
					<div className="new-app-icon col-3">
						<img src="/images/placeholder-image.png" width="200" height="200" alt="App icon placeholder"/>
					</div>
					<div className="new-app-info col-9">
						<h1>
							<span className={this.editHoverClass}>{this.master.newAppData.appName}</span>{this.renderEditIcon()}
						</h1>
						<div className="row">
							<div className="saved-app-listing-info col-4">
								<div className={`saved-app-listing-info-row`}>
									<span className={this.editHoverClass}>
										Platform: <strong>{this.master.newAppData.platform}</strong>
									</span>
								</div>
								<div className={`saved-app-listing-info-row`}>
									<span className={this.editHoverClass}>
										Listing Type: <strong>{this.master.newAppData.listingType === 'CROSSLISTED' ? 'Cross-listed' : 'Hosted'}</strong>
									</span>
								</div>
							</div>
							{this.master.newAppData.listingType === 'CROSSLISTED' &&
								<div className="saved-app-listing-info col-8">
									<div className={`saved-app-listing-info-row`}>
										<span className={this.editHoverClass}>
											{`${storeName} URL: `}
											<strong>
												<AsideLink
													to={this.master.newAppData.storeLink}
													text={this.master.newAppData.appName} />
											</strong>
										</span>
									</div>
									<div className={`saved-app-listing-info-row`}>
										<span className={this.editHoverClass}>
											{`${storeName} Version: `}<strong>{this.master.newAppData.storeVersion}</strong>
										</span>
									</div>
								</div>
							}
						</div>
					</div>
				</div>
				<div className="row save-section">
					<div className="col-7 offset-3">
						<div className="saved-app-status">DRAFT</div>
						<div className="saved-app-time-delete">
							<span>Last Saved: </span>
							<span className="saved-app-time">{utilsService.normalizedDate(lastSavedDatetime)}</span>
							<span className="saved-app-pipe"> &#124; </span>
							<span className="saved-app-time">{utilsService.normalizedDate(lastSavedDatetime, 'LT')}</span>
							<span className="saved-app-pipe"> &#124; </span>
							<button
								onClick={() => { this.handleToggleModal('deleteDraft') }}
								className="btn as-link">
								<i className="icon-trash" aria-hidden="true"></i>
								Delete Draft
								</button>
						</div>
					</div>
					<div className="save-draft-button col-2">
						{this.renderSaveDraftButton()}
					</div>
				</div>
			</div>
		);
	}

	renderEditIcon = () => {
		let markup = '', section = this.props.match.params.section;
		if(section !== 'app-review' && section !== 'app-submit' && !this.master.masterStateProps.isSavingAndChecking){
			markup = (
				<button type="button" className="new-app-info-edit-button"
					onClick={this.onEditClick}
					onMouseOver={this.onEditMouseOver}
					onMouseOut={this.onEditMouseOut}>
					<span><i className="icon-pencil ltblue" aria-hidden="true"></i>EDIT</span>
					<span className="sr-only">Edit App Name and Platform</span>
				</button>
			);
		}
		return markup;
	}

	renderSaveDraftButton = () => {
		let markup = '', section = this.props.match.params.section;
		if(section !== 'app-review' && section !== 'app-submit'){
			markup = (
				<button type="button" disabled={!this.master.newAppFormIsDirty} className="fn-primary" onClick={this.saveAndStay}>
					Save Draft
				</button>
			);
		}
		if(this.master.masterStateProps.isPartialSaving){
			markup = (
				<div className="new-app-footer-buttons">
					<button className="fn-primary" disabled={true}>
						<span><i className="icon-reload"/>Saving&hellip;</span>
					</button>
				</div>
			);
		}
		return markup;
	}

	deleteDraftModalBody = () => {
		return (
			<div>
				<p>The information you have entered will be lost permanently. It will not be submitted to FirstNet for approval.</p>
				<p>Previously approved versions of the app will not be affected.</p>
			</div>
		)
	}

	render() {
		return (
			<section className="new-app-name-platform">
				<div className="container">
					<AlertMessage />
				</div>
				{this.showAlertBar && !this.props.store.isValid &&
					<div className="container">
						<ButterBar autoFocus type="error" message="<strong>Error:</strong> Please correct the errors below." handleCloseClick={() => this.toggleAlert(false)} />
					</div>
				}
				{this.master.masterStateProps.namePlatformIsEditable
					? this.renderEditableFields()
					: this.renderRecordedFields()}
				{modalsStore.modalVisibilities.deleteDraft && <AppModal
					type='deleteDraft'
					name={(this.master.newAppData.appName ? this.master.newAppData.appName : '')}
					platform={(this.master.newAppData.platform ? this.master.newAppData.platform : '')}
					version={(this.master.newAppData.version ? this.master.newAppData.version : '')}
					title="Delete Draft?"
					body={this.deleteDraftModalBody()}
					cancelButtonText="Keep Draft"
					confirmationButtonText="Delete Draft"
					confirmationActionButtonText="Deleting"
					disableConfirmationButton={this.deleteIsLoading}
					handleToggleModal={this.handleToggleModal}
					handleConfirmationClick={this.handleDeleteDraftConfirm}
					modalIsVisible={modalsStore.modalVisibilities.deleteDraft} />}
			</section>
		);
	}
}

export default withRouter(NewAppNamePlatform);
